/**
 * Leitura da chave de acesso da NF-e (44 dígitos).
 *
 * A resposta de `/invoices` não traz `ide.serie` nem `ide.nNF` — a tela lia
 * esses dois campos e eles nunca chegaram, então o cabeçalho do documento
 * aparecia sem número e sem série. Mas o dado existe: a chave de acesso
 * **contém** ambos, em posição fixa definida pelo layout da NF-e.
 *
 * ```
 * 0..1   cUF        UF do emitente
 * 2..5   AAMM       ano e mês da emissão
 * 6..19  CNPJ       emitente
 * 20..21 mod        modelo (55 NF-e, 65 NFC-e)
 * 22..24 serie
 * 25..33 nNF        número do documento
 * 34     tpEmis
 * 35..42 cNF        código numérico
 * 43     cDV        dígito verificador
 * ```
 *
 * Derivar daqui é exato — não é estimativa: a chave é o próprio documento
 * identificando a si mesmo.
 */

export interface AccessKeyParts {
  uf: string;
  yearMonth: string;
  cnpj: string;
  model: string;
  series: number;
  number: number;
  emissionType: string;
  checkDigit: string;
}

export function parseAccessKey(accessKey: string | null | undefined): AccessKeyParts | null {
  if (!accessKey) return null;
  const digits = accessKey.replace(/\D/g, '');
  if (digits.length !== 44) return null;

  return {
    uf: digits.slice(0, 2),
    yearMonth: digits.slice(2, 6),
    cnpj: digits.slice(6, 20),
    model: digits.slice(20, 22),
    series: Number.parseInt(digits.slice(22, 25), 10),
    number: Number.parseInt(digits.slice(25, 34), 10),
    emissionType: digits.slice(34, 35),
    checkDigit: digits.slice(43, 44),
  };
}

/** Número do documento, ou `null` quando a nota ainda não tem chave. */
export function invoiceNumber(accessKey: string | null | undefined): number | null {
  const parts = parseAccessKey(accessKey);
  return parts ? parts.number : null;
}

/** Série do documento, ou `null` quando a nota ainda não tem chave. */
export function invoiceSeries(accessKey: string | null | undefined): number | null {
  const parts = parseAccessKey(accessKey);
  return parts ? parts.series : null;
}

/** Chave formatada em blocos de 4, como a DANFE imprime. */
export function formatAccessKey(accessKey: string | null | undefined): string {
  const digits = (accessKey ?? '').replace(/\D/g, '');
  if (digits.length !== 44) return accessKey ?? '';
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}
